import { Box, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { getAwaitingExpenseClaims } from "../../../api/dossier/getAwaitingExpenseClaims";
import {
  DossierTransactionFilters,
  getDossierTransactions,
} from "../../../api/dossier/getDossierTransactions";
import APFilterableList from "../../../components/APFilterableList";
import DossierSelector from "../../../components/dossier/DossierSelector";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../../components/form/APAutoComplete";
import APDateField from "../../../components/form/APDateField";
import { isDossierActive } from "../../../helpers/dossiers";
import { mergeAndSortTransactionsAndExpenseClaims } from "../../../helpers/dossierTransactions";
import { filterExpenseClaims } from "../../../helpers/expenseClaims";
import { useDossierActivationCheck } from "../../../hooks/useDossierActivationCheck";
import { useFaqs } from "../../../hooks/useFaqs";
import {
  DossierTransaction,
  DossierTransactionType,
  DossierTransactionTypeOptions,
} from "../../../types/DossierTransaction";
import { ExpenseClaim, ExpenseClaimType } from "../../../types/ExpenseClaim";
import ActiveDossierNavButtons from "../components/ActiveDossierNavButtons";
import DossierBalances from "../components/DossierBalances";
import DossierTransactionCard from "../components/DossierTransactionCard";
import ExpenseClaimCard from "../components/ExpenseClaimCard";

type DossierTransactionFilterInputs = {
  dateFrom: string;
  dateTo: string;
  category: AutoCompleteOption[] | null;
};

export const DEFAULT_PAGE_SIZE = 15;

const DossierTransactions = () => {
  const { dossierId } = useParams<{
    dossierId: string;
  }>();
  const [filters, setFilters] = useState<DossierTransactionFilters>({
    dossierId: dossierId || "",
    pageSize: DEFAULT_PAGE_SIZE,
    transactionTypes: [
      "fuel",
      "servicing",
      "tyres",
      "rego",
      "insurance",
      "finance",
      "fees",
      "pre-tax",
      "post-tax",
    ],
  });

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<DossierTransactionFilterInputs>({
    mode: "onTouched",
  });

  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");

  const { navigationCheckComplete, isLoading, isSuccess, activatedDossiers } =
    useDossierActivationCheck({
      dossierId,
    });

  const awaitingExpenseClaims = getAwaitingExpenseClaims(dossierId ?? "");

  if (awaitingExpenseClaims.isError) {
    throw awaitingExpenseClaims.error;
  }

  const selectedDossier = activatedDossiers.find(
    (dossier) => dossier.id === dossierId
  );

  const { faqProps } = useFaqs({
    pageId: "dossier-transactions-view",
    contextIds: {
      opportunityId: selectedDossier?.opportunity?.id,
    },
  });

  const filteredAwaitingExpenseClaims = useMemo(() => {
    if (!awaitingExpenseClaims.data) {
      return [];
    }
    return filterExpenseClaims(
      awaitingExpenseClaims.data,
      filters.dateFrom,
      filters.dateTo,
      filters.transactionTypes as ExpenseClaimType[],
      true
    );
  }, [awaitingExpenseClaims.data, filters]);

  useEffect(() => {
    if (dossierId && dossierId !== filters.dossierId) {
      setFilters({ ...filters, dossierId, nextPageLink: undefined });
    }
  }, [dossierId]);

  const inputsToFilters = (inputs: DossierTransactionFilterInputs) => {
    return {
      ...filters,
      dateFrom: inputs.dateFrom
        ? dayjs(inputs.dateFrom).toISOString()
        : undefined,
      dateTo: inputs.dateTo
        ? dayjs(inputs.dateTo).endOf("day").toISOString()
        : undefined,
      transactionTypes: inputs.category
        ? inputs.category.map((cat) => cat.id as DossierTransactionType)
        : undefined,
    };
  };

  const renderExpenseClaimCard = (expenseClaim: ExpenseClaim) => {
    return (
      <ExpenseClaimCard
        key={expenseClaim.id}
        primaryAmount="exGst"
        expenseClaim={expenseClaim}
        showNegativeAmount={true}
        faqs={faqProps}
        etaFaqAppLocationIdentifier="DossierTransaction-WhatIsETA"
      />
    );
  };

  const renderDossierTransactionCard = (transaction: DossierTransaction) => {
    return (
      <DossierTransactionCard key={transaction.id} transaction={transaction} />
    );
  };

  const renderTransactionOrExpenseClaimCard = (
    transactionOrExpenseClaim: DossierTransaction | ExpenseClaim
  ) => {
    if ("expenseClaimId" in transactionOrExpenseClaim) {
      return renderDossierTransactionCard(
        transactionOrExpenseClaim as unknown as DossierTransaction
      );
    }
    return renderExpenseClaimCard(
      transactionOrExpenseClaim as unknown as ExpenseClaim
    );
  };

  const renderItems = (
    items: DossierTransaction[],
    paginationHistory: string[]
  ) => {
    const renderedItems = mergeAndSortTransactionsAndExpenseClaims(
      items,
      paginationHistory.length === 0 ? filteredAwaitingExpenseClaims : []
    ).map(renderTransactionOrExpenseClaimCard);

    return renderedItems;
  };

  return (
    <APFilterableList
      key={dossierId}
      title="Transactions"
      filters={filters}
      setFilters={setFilters}
      pageSize={DEFAULT_PAGE_SIZE}
      getItems={getDossierTransactions}
      inputsToFilters={inputsToFilters}
      handleSubmit={handleSubmit}
      errors={errors}
      formFields={
        <>
          <Box
            sx={{
              display: "flex",
              columnGap: 2,
            }}
          >
            <APDateField
              name="dateFrom"
              label="Date From"
              control={control}
              defaultValue={null}
              errors={errors}
              setError={setError}
              clearErrors={clearErrors}
              maxDate={dateTo ? dayjs(dateTo) : undefined}
              clearable
            />
            <APDateField
              name="dateTo"
              label="Date To"
              control={control}
              defaultValue={null}
              errors={errors}
              setError={setError}
              clearErrors={clearErrors}
              minDate={dateFrom ? dayjs(dateFrom) : undefined}
              clearable
            />
          </Box>

          <APAutoComplete
            name="category"
            label="Category"
            options={DossierTransactionTypeOptions}
            control={control}
            validations={{ required: true }}
            errors={errors}
            defaultValue={DossierTransactionTypeOptions}
            setValue={setValue}
            multiple
          />
        </>
      }
      renderItems={renderItems}
      isLoading={isLoading || !navigationCheckComplete}
      isSuccess={isSuccess && navigationCheckComplete && !!dossierId}
      topCollapseContent={
        selectedDossier && (
          <Box mb={2}>
            <DossierSelector
              dossiers={activatedDossiers}
              selectedDossier={selectedDossier}
              redirectTo="dossierTransactions"
              showDivider
            />
          </Box>
        )
      }
      abovePaginationContent={
        <Box sx={{ display: "flex", justifyContent: "flex-end" }} mt={1}>
          <Box mr={2}>
            <Typography variant="bodySmall">Ex GST</Typography>
          </Box>
        </Box>
      }
      overrideContent={
        !selectedDossier && (
          <Typography variant="bodyMedium">Dossier is not activated</Typography>
        )
      }
      bottomContent={
        <Stack spacing={3} pt={2}>
          {selectedDossier?.currentBalance != null && (
            <Box>
              <Box mr={2}>
                <DossierBalances
                  currentBalance={selectedDossier.currentBalance}
                />
              </Box>
            </Box>
          )}
          {selectedDossier && (
            <ActiveDossierNavButtons
              dossier={selectedDossier}
              backTypes={["back", "home"]}
              navButtons={["view-budgets", "view-balances"]}
              rightNavButtons={
                isDossierActive(selectedDossier) ? ["submit-claim"] : undefined
              }
            />
          )}
          <Box sx={{ display: "flex" }}>
            {faqProps.renderAppLocation(
              "DossierTransaction-WhatIsETA",
              <Typography
                variant="bodySmall"
                color="primary"
                sx={{ textDecoration: "underline", cursor: "pointer" }}
              >
                *What is the ETA?
              </Typography>,
              true
            )}
          </Box>
        </Stack>
      }
      shouldDisplayEmptyMessage={filteredAwaitingExpenseClaims.length === 0}
    />
  );
};

export default DossierTransactions;
